import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  CircularProgress,
  Stack,
  Tabs,
  Tab,
  Drawer,
  Divider,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  DragIndicator as DragIcon,
  Lock as LockIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  getHardenedFields,
  createHardenedField,
  updateHardenedField,
  deleteHardenedField,
  reorderHardenedFields,
} from "../../services/formService";

const FIELD_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "textarea", label: "Text Area" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "Multiselect" },
  { value: "radio", label: "Radio" },
  { value: "boolean", label: "Yes/No" },
  { value: "date", label: "Date DOB" },
  { value: "file", label: "File Upload" },
  { value: "location", label: "Location" },
  { value: "multiSelectCheckbox", label: "Multi Select Checkbox" },
  { value: "gender", label: "Gender" },
  { value: "cstmState", label: "CstmState" },
];

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "hn", name: "Hinglish", flag: "🇮🇳" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "ta", name: "Tamil", flag: "🇮🇳" },
  { code: "te", name: "Telugu", flag: "🇮🇳" },
  { code: "kn", name: "Kannada", flag: "🇮🇳" },
  { code: "bn", name: "Bengali", flag: "🇮🇳" },
  { code: "gu", name: "Gujarati", flag: "🇮🇳" },
  { code: "mr", name: "Marathi", flag: "🇮🇳" },
];

const EMPTY_FIELD = {
  key: "",
  label: "",
  label_hi: "",
  label_hn: "",
  label_ta: "",
  label_te: "",
  label_kn: "",
  label_bn: "",
  label_gu: "",
  label_mr: "",
  type: "text",
  required: false,
  isFilter: false,
  isVisibleToEmployer: false,
  visibleToEmployerLabel: "",
  isRequiredInRegionFilter: false,
  options: [],
  options_hi: [],
  options_hn: [],
  options_ta: [],
  options_te: [],
  options_kn: [],
  options_bn: [],
  options_gu: [],
  options_mr: [],
};

const HardenedFieldsManager = ({ open, onClose, onFieldsUpdated, canCreate, canUpdate, canDelete }) => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [currentField, setCurrentField] = useState({ ...EMPTY_FIELD });
  const [currentLanguageTab, setCurrentLanguageTab] = useState("en");
  const [validationError, setValidationError] = useState("");
  const [snackMessage, setSnackMessage] = useState({ text: "", severity: "success" });

  // Option editing state
  const [optionLabel, setOptionLabel] = useState("");
  const [optionValue, setOptionValue] = useState("");
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);

  const loadFields = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getHardenedFields();
      const data = response?.data || [];
      setFields(Array.isArray(data) ? data : []);
    } catch (error) {
      const msg = error.response?.data?.msg || error.message || "Failed to load hardened fields";
      setSnackMessage({ text: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadFields();
    }
  }, [open, loadFields]);

  const resetFieldForm = () => {
    setCurrentField({ ...EMPTY_FIELD });
    setCurrentLanguageTab("en");
    setValidationError("");
    setOptionLabel("");
    setOptionValue("");
    setEditingOptionIndex(null);
  };

  const handleOpenCreate = () => {
    setEditingFieldId(null);
    resetFieldForm();
    setShowFieldDialog(true);
  };

  const handleOpenEdit = (field) => {
    setEditingFieldId(field._id);
    setCurrentField({
      ...EMPTY_FIELD,
      ...field,
    });
    setCurrentLanguageTab("en");
    setValidationError("");
    setShowFieldDialog(true);
  };

  const handleOpenDelete = (fieldId) => {
    setDeleteTargetId(fieldId);
    setShowDeleteDialog(true);
  };

  const handleFieldChange = (key, value) => {
    const updated = { ...currentField, [key]: value };
    if (key === "isVisibleToEmployer" && value === false) {
      updated.visibleToEmployerLabel = "";
    }
    setCurrentField(updated);
    setValidationError("");
  };

  const handleLanguageLabelChange = (value) => {
    const labelKey = currentLanguageTab === "en" ? "label" : `label_${currentLanguageTab}`;
    setCurrentField({ ...currentField, [labelKey]: value });
    setValidationError("");
  };

  const getLabelValue = () => {
    if (currentLanguageTab === "en") return currentField.label || "";
    return currentField[`label_${currentLanguageTab}`] || "";
  };

  const getOptionsValue = () => {
    const key = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    return currentField[key] || [];
  };

  // Option handlers
  const handleAddOption = () => {
    if (!optionLabel.trim()) { setValidationError("Option label is required"); return; }
    if (!optionValue.trim()) { setValidationError("Option value is required"); return; }
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    setCurrentField({
      ...currentField,
      [optionsKey]: [...(currentField[optionsKey] || []), { label: optionLabel.trim(), value: optionValue.trim() }],
    });
    setOptionLabel("");
    setOptionValue("");
    setValidationError("");
  };

  const handleEditOption = (idx) => {
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const opt = currentField[optionsKey][idx];
    setEditingOptionIndex(idx);
    setOptionLabel(typeof opt === "object" ? opt.label : opt);
    setOptionValue(typeof opt === "object" ? opt.value : opt);
  };

  const handleUpdateOption = () => {
    if (!optionLabel.trim()) { setValidationError("Option label is required"); return; }
    if (!optionValue.trim()) { setValidationError("Option value is required"); return; }
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const updated = [...currentField[optionsKey]];
    updated[editingOptionIndex] = { label: optionLabel.trim(), value: optionValue.trim() };
    setCurrentField({ ...currentField, [optionsKey]: updated });
    setOptionLabel("");
    setOptionValue("");
    setEditingOptionIndex(null);
    setValidationError("");
  };

  const handleRemoveOption = (idx) => {
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    setCurrentField({
      ...currentField,
      [optionsKey]: currentField[optionsKey].filter((_, i) => i !== idx),
    });
    if (editingOptionIndex === idx) {
      setEditingOptionIndex(null);
      setOptionLabel("");
      setOptionValue("");
    }
  };

  const handleSaveField = async () => {
    if (!currentField.key?.trim()) { setValidationError("Key is required"); return; }
    if (!currentField.label?.trim()) { setValidationError("Label is required"); return; }
    if (!currentField.type?.trim()) { setValidationError("Type is required"); return; }

    if (["dropdown", "radio", "multiselect", "multiSelectCheckbox", "gender"].includes(currentField.type)) {
      if (!currentField.options || currentField.options.length === 0) {
        setValidationError(`Options are required for ${currentField.type}`);
        return;
      }
    }

    try {
      // Build payload — strip _id, __v, createdAt, etc.
      const payload = {};
      const allowedKeys = [
        "key", "label", "type", "required", "isFilter",
        "isVisibleToEmployer", "visibleToEmployerLabel",
        "isRequiredInRegionFilter", "options", "order",
        "label_hi", "label_hn", "label_ta", "label_te", "label_kn", "label_bn", "label_gu", "label_mr",
        "options_hi", "options_hn", "options_ta", "options_te", "options_kn", "options_bn", "options_gu", "options_mr",
      ];
      const optionsKeys = [
        "options", "options_hi", "options_hn", "options_ta", "options_te",
        "options_kn", "options_bn", "options_gu", "options_mr",
      ];
      allowedKeys.forEach((k) => {
        if (currentField[k] !== undefined) {
          if (optionsKeys.includes(k) && Array.isArray(currentField[k])) {
            // Strip _id from option objects added by MongoDB
            payload[k] = currentField[k].map(({ _id, ...rest }) => rest);
          } else {
            payload[k] = currentField[k];
          }
        }
      });

      if (editingFieldId) {
        await updateHardenedField(editingFieldId, payload);
        setSnackMessage({ text: "Hardened field updated successfully", severity: "success" });
      } else {
        await createHardenedField(payload);
        setSnackMessage({ text: "Hardened field created successfully", severity: "success" });
      }

      setShowFieldDialog(false);
      resetFieldForm();
      loadFields();
      if (onFieldsUpdated) onFieldsUpdated();
    } catch (error) {
      const msg = error.response?.data?.msg || error.message || "Failed to save hardened field";
      setValidationError(msg);
    }
  };

  const handleDeleteField = async () => {
    try {
      await deleteHardenedField(deleteTargetId);
      setSnackMessage({ text: "Hardened field deleted", severity: "success" });
      setShowDeleteDialog(false);
      setDeleteTargetId(null);
      loadFields();
      if (onFieldsUpdated) onFieldsUpdated();
    } catch (error) {
      const msg = error.response?.data?.msg || error.message || "Failed to delete hardened field";
      setSnackMessage({ text: msg, severity: "error" });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "600px", md: "650px" },
          boxSizing: "border-box",
        },
      }}
    >
      {/* Header */}
      <Box sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        p: 2, borderBottom: "2px solid #e0e0e0", bgcolor: "#f5f5f5",
        position: "sticky", top: 0, zIndex: 1,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LockIcon color="warning" fontSize="large" />
          <Box>
            <Typography variant="h6" fontWeight={700}>System Fields</Typography>
            <Typography variant="caption" color="text.secondary">
              These fields are auto-included in every new form
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
        {snackMessage.text && (
          <Alert
            severity={snackMessage.severity}
            onClose={() => setSnackMessage({ text: "", severity: "success" })}
            sx={{ mb: 2 }}
          >
            {snackMessage.text}
          </Alert>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Chip label={`${fields.length} system fields`} color="warning" variant="outlined" size="small" />
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleOpenCreate} disabled={!canCreate}>
            Add Field
          </Button>
        </Stack>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : fields.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Typography color="text.secondary">
              No system fields configured yet. Click "Add Field" to create the first one.
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f9f9f9" }}>
                  <TableCell sx={{ fontWeight: 700, width: 50 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Required</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, idx) => (
                  <TableRow key={field._id} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                        {field.key}
                      </Typography>
                    </TableCell>
                    <TableCell>{field.label}</TableCell>
                    <TableCell>
                      <Chip
                        label={FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={field.required ? "Yes" : "No"}
                        size="small"
                        color={field.required ? "error" : "default"}
                        variant="outlined"
                        sx={{ fontSize: "0.7rem" }}
                      />

                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => handleOpenEdit(field)} color="primary" title="Edit" disabled={!canUpdate}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleOpenDelete(field._id)} color="error" title="Delete" disabled={!canDelete}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: "2px solid #e0e0e0", bgcolor: "#f5f5f5", position: "sticky", bottom: 0 }}>
        <Button variant="outlined" fullWidth onClick={onClose}>Close</Button>
      </Box>

      {/* Create/Edit Field Dialog */}
      <Dialog
        open={showFieldDialog}
        onClose={(_, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") setShowFieldDialog(false);
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingFieldId ? "Edit System Field" : "Add System Field"}</DialogTitle>
        <DialogContent sx={{ pt: "10px !important" }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>{validationError}</Alert>
          )}

          <Stack spacing={2}>
            <TextField
              label="Field Key"
              value={currentField.key}
              onChange={(e) => handleFieldChange("key", e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., full_name"
              helperText="Unique key identifier (snake_case recommended)"
            />

            <FormControl fullWidth size="small">
              <InputLabel>Field Type</InputLabel>
              <Select
                value={currentField.type}
                label="Field Type"
                onChange={(e) => handleFieldChange("type", e.target.value)}
              >
                {FIELD_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Language Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={currentLanguageTab}
                onChange={(_, v) => setCurrentLanguageTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ "& .MuiTab-root": { fontSize: "0.75rem", minWidth: 70, py: 0.5 } }}
              >
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <Tab key={lang.code} label={`${lang.flag} ${lang.code.toUpperCase()}`} value={lang.code} title={lang.name} />
                ))}
              </Tabs>
            </Box>

            <TextField
              label={`Label (${currentLanguageTab.toUpperCase()})`}
              value={getLabelValue()}
              onChange={(e) => handleLanguageLabelChange(e.target.value)}
              fullWidth
              size="small"
              placeholder={`Enter label in ${SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguageTab)?.name}`}
            />

            {/* Options for dropdown/radio/gender/multiselect types */}
            {["dropdown", "radio", "multiselect", "multiSelectCheckbox", "gender"].includes(currentField.type) && (
              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                  Options ({currentLanguageTab.toUpperCase()})
                </Typography>

                {getOptionsValue().map((opt, idx) => (
                  <Box key={idx} sx={{ display: "flex", gap: 0.5, mb: 0.5, alignItems: "center", p: 0.5, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                        {typeof opt === "object" ? opt.label : opt}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Value: {typeof opt === "object" ? opt.value : opt}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => handleEditOption(idx)} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleRemoveOption(idx)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </Box>
                ))}

                <Stack spacing={1} sx={{ mt: 1 }}>
                  <TextField
                    label="Option Label"
                    size="small"
                    value={optionLabel}
                    onChange={(e) => setOptionLabel(e.target.value)}
                  />
                  <TextField
                    label="Option Value"
                    size="small"
                    value={optionValue}
                    onChange={(e) => setOptionValue(e.target.value)}
                  />
                  <Stack direction="row" spacing={1}>
                    {editingOptionIndex !== null && (
                      <Button variant="outlined" size="small" sx={{ flex: 1 }} onClick={() => { setEditingOptionIndex(null); setOptionLabel(""); setOptionValue(""); }}>
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ flex: 1 }}
                      onClick={editingOptionIndex !== null ? handleUpdateOption : handleAddOption}
                    >
                      {editingOptionIndex !== null ? "Update Option" : "Add Option"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Required</InputLabel>
              <Select
                value={currentField.required ? "yes" : "no"}
                label="Required"
                onChange={(e) => handleFieldChange("required", e.target.value === "yes")}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>

            <Divider />

            <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Employer Settings</Typography>

              {currentField.type !== "location" && (
                <FormControlLabel
                  control={
                    <Switch checked={currentField.isFilter || false} onChange={(e) => handleFieldChange("isFilter", e.target.checked)} />
                  }
                  label="Is Filter"
                />
              )}

              <Box sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1, mt: 1, bgcolor: "#fafafa" }}>
                <FormControlLabel
                  control={
                    <Switch checked={currentField.isVisibleToEmployer || false} onChange={(e) => handleFieldChange("isVisibleToEmployer", e.target.checked)} />
                  }
                  label="Visible to Employer"
                />
                {currentField.isVisibleToEmployer && (
                  <TextField
                    label="Employer Visible Label"
                    value={currentField.visibleToEmployerLabel || ""}
                    onChange={(e) => handleFieldChange("visibleToEmployerLabel", e.target.value)}
                    fullWidth
                    size="small"
                    inputProps={{ maxLength: 40 }}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              {currentField.type === "location" && (
                <FormControlLabel
                  control={
                    <Switch checked={currentField.isRequiredInRegionFilter || false} onChange={(e) => handleFieldChange("isRequiredInRegionFilter", e.target.checked)} />
                  }
                  label="Required in Region Filter"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFieldDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveField} variant="contained">{editingFieldId ? "Update" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={showDeleteDialog}
        onClose={(_, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") setShowDeleteDialog(false);
        }}
        disableEscapeKeyDown
      >
        <DialogTitle>Delete System Field</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this system field? New forms will no longer include it,
            but existing forms will not be affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteField} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default HardenedFieldsManager;
